import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, forkJoin, Subscription } from 'rxjs';
import { FuncionesServiceProvider, MenuServiceProvider, UsuarioServiceProvider } from '../index.providers';
import { AlertController, ModalController, NavController, Platform } from '@ionic/angular';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { ApiService } from 'src/app/api.service';
import { environment } from 'src/environments/environment';
import { NativeMarket } from "@capacitor-community/native-market";
import { PlatformUpgradeAvailableComponent } from 'src/app/components/platform-modal/platform-upgrade-available/platform-upgrade-available.component';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { LanguageService } from '../language-service/language.service';
import { PushNotificationsService } from '../fcm-service/push-notifications-service';
import { AppPlatformService } from '../platform/app-platform.service';
import { map, tap, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { BiometricAuthService } from '../biometric-auth/biometric-auth.service';
import { ThemeColorsService } from '../theme-colors/theme-colors.service';

interface SavedTheme {
  serviceBase: string;
  [key: string]: any;
}

interface PreLoginBrand {
  baseUrl: string;
  workspaceName: string;
}
import { isPerformanceMockEnabled, PERFORMANCE_BASE_URL_STORAGE_KEY } from '../modulo-desempeno/mock/mock-config';
import { performanceAccent } from "../theme/performance-accent";
import { clearThemeSnapshot, saveThemeSnapshot } from "../theme/theme-snapshot";
import { hayDestinoPendiente, tomarDestinoPendiente } from "../pending-deep-link/pending-deep-link";

declare const window: any;
@Injectable({
  providedIn: 'root'
})
export class LoginMultiModuloService {
  modulesOptions = [];
  public workspacesList = new BehaviorSubject<any>(null);
	public workspacesList$ = this.workspacesList.asObservable();
  data_empresa;
  language:string = "es";
  /** Única suscripción viva de `buildFinalTheme` a `configBrand`: cada llamada cierra la anterior. */
  private themeSubscription?: Subscription;

  /**
   * Llave donde se persiste `preLoginBrand`. La marca elegida en pre-login (o
   * resuelta por subdominio) vivía solo en memoria y se perdía en cada recarga
   * o deep link, dejando sin `company` a los flujos pre-login que pegan al
   * master (recuperar contraseña, ayuda al coordinador, SSO, cambio de
   * contraseña por enlace de correo).
   */
  private static readonly PRE_LOGIN_BRAND_KEY = "preLoginBrand";

  private _preLoginBrand: PreLoginBrand | null = null;

  get preLoginBrand(): PreLoginBrand | null {
    if (this._preLoginBrand) return this._preLoginBrand;

    // Rehidratación tras un F5 o al abrir un deep link con la app recién arrancada.
    try {
      const stored = localStorage.getItem(LoginMultiModuloService.PRE_LOGIN_BRAND_KEY);
      this._preLoginBrand = stored ? JSON.parse(stored) : null;
    } catch {
      this._preLoginBrand = null;
    }

    return this._preLoginBrand;
  }
  set preLoginBrand(value: PreLoginBrand | null) {
    this._preLoginBrand = value;

    // `preLoginBrand = null` (path "marca guardada", login.page.ts) debe borrar
    // también la copia persistida para no arrastrar una marca obsoleta.
    if (value) {
      localStorage.setItem(LoginMultiModuloService.PRE_LOGIN_BRAND_KEY, JSON.stringify(value));
    } else {
      localStorage.removeItem(LoginMultiModuloService.PRE_LOGIN_BRAND_KEY);
    }
  }

  /**
   * Borra solo la copia persistida de `preLoginBrand`, conservando la de memoria.
   * Se llama tras un login exitoso: a partir de ahí la fuente de verdad de la
   * marca es `api_urls` (ver `getCurrentBrandKey`).
   *
   * La copia en memoria se conserva porque justo después, en ese mismo flujo,
   * `handleThemeMergeAndApplyFinalTheme` emite `configBrand` y login.page.ts
   * responde con `refreshBiometricEnabledForActiveBrand` -> `getActiveBrandKey`.
   * En ese instante `data_empresa.serviceBase` aún está vacío (`getBaseThemeConfig`
   * solo inyecta `serviceBase` dentro de `savedThemes`, no en el config que
   * devuelve), así que sin la copia en memoria el brandKey saldría `null` y el
   * botón biométrico se ocultaría.
   */
  clearPersistedPreLoginBrand() {
    localStorage.removeItem(LoginMultiModuloService.PRE_LOGIN_BRAND_KEY);
  }

  /**
   * Marca del viaje SSO en curso.
   *
   * El retorno del proveedor es un arranque en frío (en web el SSO se abre con
   * `window.open`), así que el login depende de que `preLoginBrand` haya
   * sobrevivido al viaje. Esta llave es el respaldo: se escribe al salir al
   * proveedor y se consume al volver con error, sin depender de qué haya pasado
   * con `preLoginBrand` mientras tanto. Ninguna limpieza la toca.
   */
  private static readonly SSO_PENDING_BRAND_KEY = "ssoPendingBrand";

  /** Vida útil del snapshot: pasado ese plazo el viaje SSO ya no es el actual. */
  private static readonly SSO_PENDING_BRAND_TTL_MS = 15 * 60 * 1000;

  /** Guarda la marca activa antes de salir al proveedor SSO. */
  saveSsoPendingBrand(brand: PreLoginBrand) {
    if (!environment.APP_MULTIMARCA || !brand?.baseUrl) return;

    localStorage.setItem(
      LoginMultiModuloService.SSO_PENDING_BRAND_KEY,
      JSON.stringify({ ...brand, ts: Date.now() })
    );
  }

  /**
   * Devuelve la marca del viaje SSO y borra el snapshot: es de un solo uso, para
   * que un viaje viejo nunca se cuele en un flujo posterior. `null` si no hay,
   * si está corrupto o si caducó.
   */
  consumeSsoPendingBrand(): PreLoginBrand | null {
    if (!environment.APP_MULTIMARCA) return null;

    const stored = this.readStoredJson(LoginMultiModuloService.SSO_PENDING_BRAND_KEY);
    this.clearSsoPendingBrand();

    if (!stored?.baseUrl) return null;
    if (Date.now() - (stored.ts || 0) > LoginMultiModuloService.SSO_PENDING_BRAND_TTL_MS) return null;

    return { baseUrl: stored.baseUrl, workspaceName: stored.workspaceName };
  }

  clearSsoPendingBrand() {
    localStorage.removeItem(LoginMultiModuloService.SSO_PENDING_BRAND_KEY);
  }

  // Código de plataforma que espera el backend para obtener el token de Desempeño.
  private readonly PERFORMANCE_PLATFORM_CODE = 'performance';
  // Clave localStorage que recuerda, POR usuario, la última plataforma en la que
  // estuvo (Desempeño u origen). Persiste tras el logout (no se limpia en
  // clearStorage) y al estar indexada por id de usuario, la preferencia de un
  // usuario no afecta al siguiente que se loguee en el mismo navegador.
  private readonly PERFORMANCE_LAST_PLATFORM_KEY = 'performanceLastPlatformByUser';

  constructor(
    private router: Router,
    private up: UsuarioServiceProvider,
    private navCtrl: NavController,
    private titleService: Title,
    @Inject(DOCUMENT) private document: any,
    private api: ApiService,
		private alertCtrl: AlertController,
    private platform: Platform,
    private modalController: ModalController,
    private languageService: LanguageService,
    private pushNotificationsService : PushNotificationsService,
    private appPlatformService:AppPlatformService,
    private mp: MenuServiceProvider,
    private http: HttpClient,
    private biometric: BiometricAuthService,
    private themeColorsService: ThemeColorsService,
    private fp: FuncionesServiceProvider,

  ) {
    this.languageService.getCurrentLang().subscribe(lang => {
      this.language = lang;
    });
  }

  setWorkspacesList(data){
    this.modulesOptions = data;
		this.workspacesList.next(this.modulesOptions);
	}

  goTo(route) {
    this.router.navigate([`${route}`]);
  }

  routeUserToPlatform(data_usuario, isRefreshRequired = false) {
    // if(data_usuario?.user_induccion_aprendizaje) { //usuario con acceso a aprendizaje e inducción
    //   this.up.setPlatformType = 'capacitacion';
    //   this.navCtrl.navigateRoot("anuncios");
    //   localStorage.setItem("select_page", "anuncios");
    // }
    // platform_type identifica la plataforma de ORIGEN (inducción/capacitación) y se
    // fija SIEMPRE primero: lo usan el fallback de abajo y el label del acceso de
    // origen en el selector, incluso cuando se reanuda directo en Desempeño.
    this.up.setPlatformType = data_usuario?.onboarding ? 'induction' : 'capacitacion';

    // Atajo de UX: si este usuario cerró sesión estando en Desempeño, se reanuda
    // ahí directamente (evita el flujo largo por el selector). Requiere que el usuario
    // siga teniendo acceso (`has_performance`): si lo perdió, el atajo lo llevaría a una
    // plataforma que ya no le corresponde. Un deeplink pendiente es intención explícita
    // del usuario y siempre gana sobre la preferencia.
    const userId = data_usuario?.id ?? this.up.getIdUsuario;
    const hasPendingDeepLink = hayDestinoPendiente();
    const hasPerformance = data_usuario?.has_performance === true;
    if (!hasPerformance) {
      // Sin acceso a Desempeño, un flag viejo del navegador haría que cargarFunciones()
      // arme el menú de Desempeño sobre contenido de capacitación/inducción.
      localStorage.setItem("isPerformanceAccessSelected", "false");
    }
    if (!hasPendingDeepLink && hasPerformance && this.getLastPlatform(userId) === 'performance') {
      this.resumeInPerformance(isRefreshRequired);
      return;
    }

    // Enlace compartido que se abrió sin sesión (p. ej. un documento de Isa Drive):
    // se entra directo ahí en vez de a la ruta default, sea inducción o capacitación.
    const destinoPendiente = tomarDestinoPendiente();
    if (destinoPendiente) {
      this.navCtrl.navigateRoot(destinoPendiente, { state: { desdeEnlace: true } });
      return;
    }

    if(data_usuario.onboarding) { // usuario con acceso a inducción
      // this.up.setPlatformType = 'induction';
      if(data_usuario.supervisor_induccion) {
        this.navCtrl.navigateRoot("procesos");
        localStorage.setItem("select_page", "procesos");
      }
      else {
        if(data_usuario.processes?.length) {
          this.navCtrl.navigateRoot("home");
          localStorage.setItem("select_page", "home");
        } else {
          this.navCtrl.navigateRoot("process-not-found");
        }
      };

      if(isRefreshRequired) {
        setTimeout(() => {
          this.refreshPage();
        }, 40);
      };
    }
    else {
      // this.up.setPlatformType = 'capacitacion';
      // this.navCtrl.navigateRoot("anuncios");
      // localStorage.setItem("select_page", "anuncios");
      this.navCtrl.navigateRoot("anuncios");
      localStorage.setItem("select_page", "anuncios");
      if(isRefreshRequired) {
        setTimeout(() => {
          this.refreshPage();
        }, 40);
      }
    }
  }

  /**
   * Reanuda la sesión directamente en Desempeño tras el login (cuando el usuario
   * cerró sesión estando ahí). Replica lo que hace el selector `toPerformanceModule`:
   * garantiza el token, marca el flag de plataforma y navega a `performance`.
   *
   * El flag se fija de forma optimista ANTES del handshake del token porque el
   * caller del login llama a `cargarFunciones()` justo después de `routeUserToPlatform`
   * (sincrónicamente): así el menú ya se arma para Desempeño. Si el token falla, se
   * revierte el flag, se reconstruye el menú de origen y se navega al origen.
   */
  private async resumeInPerformance(isRefreshRequired = false) {
    localStorage.setItem("isPerformanceAccessSelected", "true");
    const tokenReady = await this.ensurePerformanceToken();
    if (!tokenReady) {
      localStorage.setItem("isPerformanceAccessSelected", "false");
      this.fp.cargarFunciones();
      this.navigateToOriginPlatform(isRefreshRequired);
      return;
    }
    this.navCtrl.navigateRoot("performance");
    if (isRefreshRequired) {
      setTimeout(() => {
        this.refreshPage();
      }, 40);
    }
  }

  /**
   * Navegación a la plataforma de ORIGEN (inducción/capacitación) según
   * `platform_type` y los datos del usuario. Se usa como fallback cuando no se pudo
   * reanudar en Desempeño y cuando `PerformanceAccessGuard` le niega la entrada al
   * módulo. Requiere que `platform_type` ya esté fijado.
   */
  navigateToOriginPlatform(isRefreshRequired = false) {
    const userData = JSON.parse(localStorage.getItem("user_data") || "null");
    if (this.up.getPlatformType === 'induction') {
      if (userData?.supervisor_induccion) {
        this.mp.setSelectedPageRoute("procesos");
        this.navCtrl.navigateRoot("procesos");
        localStorage.setItem("select_page", "procesos");
      } else if (userData?.processes?.length) {
        this.mp.setSelectedPageRoute("home");
        this.navCtrl.navigateRoot("home");
        localStorage.setItem("select_page", "home");
      } else {
        this.navCtrl.navigateRoot("process-not-found");
      }
    } else {
      this.navCtrl.navigateRoot("anuncios");
      localStorage.setItem("select_page", "anuncios");
    }
    if (isRefreshRequired) {
      setTimeout(() => {
        this.refreshPage();
      }, 40);
    }
  }

  /**
   * Recuerda, para el usuario actualmente logueado, la última plataforma en la que
   * estuvo. Se llama al alternar plataforma desde el selector. El valor persiste al
   * cerrar sesión (no se limpia en clearStorage) e indexa por id de usuario para no
   * afectar a otros usuarios que se logueen en el mismo navegador.
   */
  rememberCurrentPlatform(platform: 'performance' | 'origin') {
    const userId = this.up.getIdUsuario;
    if (userId === null || userId === undefined || userId === '') return;
    const map = this.readLastPlatformMap();
    map[userId] = platform;
    localStorage.setItem(this.PERFORMANCE_LAST_PLATFORM_KEY, JSON.stringify(map));
  }

  /**
   * Última plataforma recordada para un usuario. Default `'origin'` (ausencia de
   * registro o storage inválido = flujo normal de origen).
   */
  private getLastPlatform(userId: any): 'performance' | 'origin' {
    if (userId === null || userId === undefined || userId === '') return 'origin';
    const map = this.readLastPlatformMap();
    return map[userId] === 'performance' ? 'performance' : 'origin';
  }

  private readLastPlatformMap(): { [userId: string]: string } {
    try {
      return JSON.parse(localStorage.getItem(this.PERFORMANCE_LAST_PLATFORM_KEY) || "{}") || {};
    } catch {
      return {};
    }
  }

  /**
   * Garantiza que exista el token de la plataforma de Desempeño antes de navegar
   * hacia ella. Solo la primera vez que el usuario entra a Desempeño en la sesión
   * se pide al backend (`platform-login-token`) usando el token de sesión original
   * del login; el nuevo token (y la `baseUrl` del backend de Desempeño) se cachean
   * en `performance_token` / `performance_base_url`. En navegaciones posteriores se
   * reutiliza lo cacheado (no se vuelve a pedir). Al volver a aprendizaje/inducción
   * el interceptor sigue usando el token original, porque el token de Desempeño
   * solo se aplica a los request de su propia base.
   *
   * Devuelve `true` si hay token disponible para los request de Desempeño.
   */
  /**
   * Acceso a la plataforma de Desempeño según el flag `has_performance` que envía el
   * backend en la key `usuario` del login (persistido tal cual en `user_data`). Criterio
   * estricto: solo `true` habilita el acceso; `false`, `null` o ausencia del flag = sin
   * acceso. Lo consumen los dos menús laterales para mostrar u ocultar el acceso.
   */
  get hasPerformanceAccess(): boolean {
    try {
      return JSON.parse(localStorage.getItem("user_data") || "null")?.has_performance === true;
    } catch {
      return false;
    }
  }

  async ensurePerformanceToken(): Promise<boolean> {
    // Con el mock de Desempeño activo, los request los responde el interceptor
    // mock y no requieren un token real: se omite el handshake con el backend.
    if (isPerformanceMockEnabled()) return true;
    if (localStorage.getItem("performance_token")) return true;
    try {
      const res = await this.api.platformLoginToken(this.PERFORMANCE_PLATFORM_CODE);
      const token = res?.data?.token;
      // El backend envía la base de la API de Desempeño junto con el token; se
      // cachea para no hardcodearla (la consumen el servicio de API y el interceptor).
      const baseUrl = res?.data?.baseUrl;
      if (token) {
        localStorage.setItem("performance_token", token);
        if (baseUrl) localStorage.setItem(PERFORMANCE_BASE_URL_STORAGE_KEY, baseUrl);
        return true;
      }
      return false;
    } catch (error) {
      console.log("ensurePerformanceToken error:", error);
      return false;
    }
  }

  refreshPage() {
    const currentUrl = this.router.url;
    // console.log("refreshPage:  ", currentUrl)
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }
  // routeUserToPlatform(data_usuario) {
  //   if(data_usuario.user_induccion_aprendizaje) {
  //     this.up.setPlatformType = 'capacitacion';
  //     this.navCtrl.navigateRoot("anuncios");
  //     localStorage.setItem("select_page", "anuncios");
  //   }
  //   else if(data_usuario.onboarding) {
  //     this.up.setPlatformType = 'induction';
  //     if(data_usuario.supervisor_induccion) {
  //       this.navCtrl.navigateRoot("procesos");
  //       localStorage.setItem("select_page", "procesos");
  //     }
  //     else {
  //       this.navCtrl.navigateRoot("home");
  //       localStorage.setItem("select_page", "home");
  //     }
  //   }
  //   else {
  //     this.up.setPlatformType = 'capacitacion';
  //     // this.navCtrl.navigateRoot("anuncios");
  //     // localStorage.setItem("select_page", "anuncios");
  //     const checkDeepLink = 	localStorage.getItem("pendingDeepLink");
  //     if(checkDeepLink) {
  //       this.router.navigateByUrl(checkDeepLink);
  //       localStorage.removeItem("pendingDeepLink");
  //     } else {
  //       this.navCtrl.navigateRoot("anuncios");
  //       localStorage.setItem("select_page", "anuncios");
  //     }
  //   }
  // }


  public handleThemeMergeAndApplyFinalTheme(advancedCustomization, baseTheme, isSingleBrand) {
    if(advancedCustomization) localStorage.setItem("loggedCustomConfig", JSON.stringify(advancedCustomization));
    const formatedData = this.mergeThemeConfig(advancedCustomization, baseTheme);
    this.buildFinalTheme(formatedData , isSingleBrand);
  }

  private mergeThemeConfig(advancedCustomization, baseTheme) {
    let themeObject = {
      ...baseTheme,
      completed_courses_logo : advancedCustomization?.completed_courses_logo || baseTheme.completed_courses_logo,
      enrolled_courses_logo : advancedCustomization?.enrolled_courses_logo || baseTheme.enrolled_courses_logo,
      diplomas_logo : advancedCustomization?.diplomas_logo || baseTheme.diplomas_logo,
      color_primario_app: advancedCustomization?.color_primario_app || baseTheme.color_primario_app,
      color_secundario_app: advancedCustomization?.color_secundario_app || baseTheme.color_secundario_app,
      color_terciario_app: advancedCustomization?.color_terciario_app || baseTheme.color_terciario_app,
    };
    return themeObject;
  }

  private buildFinalTheme(configObj, marcaPropia = false) {
		let config = configObj;
		// console.log("verificando: ",configObj);

		if(configObj && !configObj.color_primario_app) {
			//en caso lleguen los colores en null
			config.color_primario_app = "#5458e9";
			config.color_secundario_app = "#58c1e5";
		}

		// Sin cerrar la anterior, cada llamada sumaba una suscripción que seguía repintando con su
		// `marcaPropia` viejo: una de marca no propia volvía a escribir `config_ambiente` después.
		this.themeSubscription?.unsubscribe();
		this.up.setConfig(config);
		this.themeSubscription = this.up.configBrand.subscribe((res) => {
			if(res){
				// console.log("config actualizada: ", res);

				this.data_empresa = res;
				if(!marcaPropia) {
					localStorage.setItem("config_ambiente", JSON.stringify(this.data_empresa));
				}
				let title = this.getTitle(this.router.routerState, this.router.routerState.root);

				title.length
				? this.titleService.setTitle(this.data_empresa.titulo ? this.data_empresa.titulo : "Cursalab" + " | " + title[0])
				: this.titleService.setTitle(this.data_empresa.titulo ? this.data_empresa.titulo : "Cursalab");
				if(this.data_empresa && this.data_empresa.icono) {
					const favicon = document.getElementById("favicon") as HTMLLinkElement;
					favicon.href = this.data_empresa.icono;
				};

				let result_pri;
				let result_sec;
				let rgb;

				try {
					result_pri = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
						this.data_empresa.color_primario_app
					);
					result_sec = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
						this.data_empresa.color_secundario_app
					);

					//red,green,blue(primary & secondary)
					rgb = {
						rp: parseInt(result_pri[1], 16),
						gp: parseInt(result_pri[2], 16),
						bp: parseInt(result_pri[3], 16),
						rs: parseInt(result_sec[1], 16),
						gs: parseInt(result_sec[2], 16),
						bs: parseInt(result_sec[3], 16),
					};

				} catch (error) {
					console.log(error);
					result_pri = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
						"#5458e9"
					);
					result_sec = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
						"#58c1e5"
					);

					//red,green,blue(primary & secondary)
					rgb = {
						rp: parseInt(result_pri[1], 16),
						gp: parseInt(result_pri[2], 16),
						bp: parseInt(result_pri[3], 16),
						rs: parseInt(result_sec[1], 16),
						gs: parseInt(result_sec[2], 16),
						bs: parseInt(result_sec[3], 16),
					};
				}
				let rootelement = this.document.documentElement;
				// Foto de lo que queda escrito en `:root`: `index.html` la repinta antes del primer
				// fotograma de la recarga. Se anota lo mismo que se escribe, sin recalcular nada.
				const themeVars: Record<string, string> = {};
				const setVar = (name: string, value: any) => {
					rootelement.style.setProperty(name, value);
					if (value === null || value === undefined || value === "") delete themeVars[name];
					else themeVars[name] = String(value);
				};
				// colores de config
				setVar("--primary", this.data_empresa.color_primario_app);
				setVar(
					"--ion-color-primary",
					this.data_empresa.color_primario_app
				);
				//color secundario
				setVar("--secondary", this.data_empresa.color_secundario_app);
				setVar(
					"--ion-color-secondary",
					this.data_empresa.color_secundario_app
				);
				setVar(
					"--tertiary",
					this.data_empresa.color_terciario_app
						? this.data_empresa.color_terciario_app
						: this.data_empresa.color_secundario_app
				);
				setVar(
					"--performance-accent",
					performanceAccent(
						this.data_empresa.color_primario_app,
						this.data_empresa.color_secundario_app,
						this.data_empresa.color_terciario_app
					)
				);
				setVar(
					"--ion-color-tertiary",
					this.data_empresa.color_terciario_app
						? this.data_empresa.color_terciario_app
						: this.data_empresa.color_secundario_app
				);
				if (this.data_empresa.template !== "template-inretail") {
					setVar(
						"--quaternary",
						this.data_empresa.color_terciario_app ? this.data_empresa.color_terciario_app : this.data_empresa.color_secundario_app
					);
				}

				// variantes
				setVar(
					"--primary-focused",
					`rgb(${5.4*rgb.rp/10}, ${6.4*rgb.gp/10}, ${8.8*rgb.bp/10})`
					//`rgb(${6*rgb.rp/10}, ${6*rgb.gp/10}, ${6*rgb.bp/10})`   original
				);
				setVar(
					"--primary-light",
					`rgb(${18*rgb.rp/10}, ${17.5*rgb.gp/10}, ${12.2*rgb.bp/10})`
				);
        setVar(
          "--primary-semi-light",
            `rgb(${rgb.rp}, ${rgb.gp}, ${rgb.bp},0.5)`
        );
				setVar(
					"--primary-light-variant",
					`rgb(${rgb.rp}, ${rgb.gp}, ${rgb.bp},0.3)`
				);
        setVar(
          '--primary-medium-light-variant',
          `rgb(${rgb.rp}, ${rgb.gp}, ${rgb.bp},0.2)`
        );
				setVar(
					"--primary-ultra-light-variant",
					`rgb(${rgb.rp}, ${rgb.gp}, ${rgb.bp}, 0.12)`
				);
				setVar(
					"--primary-super-ultra-light-variant",
					`rgb(${rgb.rp}, ${rgb.gp}, ${rgb.bp}, 0.05)`
				);
				setVar(
					"--primary-super-ultra-light-second-variant",
					`rgb(${rgb.rp}, ${rgb.gp}, ${rgb.bp}, 0.02)`
				);
				//color secundario
				setVar(
					"--secondary-focused",
					`rgb(${8.6*rgb.rs/10}, ${9.6*rgb.gs/10}, ${9.4*rgb.bs/10})`
				);

				setVar(
					"--secondary-blur",
					`rgba(${0*rgb.rs/10}, ${9*rgb.gs/10}, ${11.5*rgb.bs/10}, 0.65)`
				);
        setVar(
          '--secondary-light-variant',
          `rgb(${rgb.rs}, ${rgb.gs}, ${rgb.bs},0.4)`
        );
        setVar(
          '--secondary-ultra-light-variant',
          `rgb(${rgb.rs}, ${rgb.gs}, ${rgb.bs},0.3)`
        );
        setVar(
          '--secondary-super-ultra-light-variant',
          `rgb(${rgb.rs}, ${rgb.gs}, ${rgb.bs},0.1)`
        );

        const darkSemi = this.darkenRgb(rgb.rp, rgb.gp, rgb.bp, 0.2); // 20% más oscuro
        const darkVariant = this.darkenRgb(rgb.rp, rgb.gp, rgb.bp, 0.4); // 40% más oscuro
        const darkMedium = this.darkenRgb(rgb.rp, rgb.gp, rgb.bp, 0.6); // 60% más oscuro
        setVar(
          "--primary-semi-dark",
          `rgb(${darkSemi.r}, ${darkSemi.g}, ${darkSemi.b})`
        );
        setVar(
          "--primary-dark-variant",
          `rgb(${darkVariant.r}, ${darkVariant.g}, ${darkVariant.b})`
        );
        setVar(
          "--primary-medium-dark-variant",
          `rgb(${darkMedium.r}, ${darkMedium.g}, ${darkMedium.b})`
        );

				saveThemeSnapshot(themeVars);
			}
		});

	}

  darkenRgb(r, g, b, factor) {
    return {
      r: Math.round(r * (1 - factor)),
      g: Math.round(g * (1 - factor)),
      b: Math.round(b * (1 - factor))
    };
  }

  getTitle(state, parent) {
		var data = [];

		if (parent && parent.snapshot.data && parent.snapshot.data.title) {
			data.push(parent.snapshot.data.title);
		}

		if (state && parent) {
			data.push(...this.getTitle(state, state.firstChild(parent)));
		}
		return data;
	}

  setUserProviderConfig(res) {
    if(res.usuario?.userLanguage) {
      localStorage.setItem('preferredLanguage', res.usuario.userLanguage);
      this.languageService.setDefaultLanguage();
    };
    this.up.setIdUsuario = res.usuario.id;
    this.up.setConfigData = JSON.stringify(res.config_data);
    this.up.setBucketBaseUrl = res.bucket_base_url;
    this.up.token = res.access_token;
    localStorage.setItem("user_data", JSON.stringify(res.usuario));
    this.up.data_usuario.next(res.usuario);
    this.up.save_storage();
  }

  async logoutPreviousSession() {
    try {
      await this.api.logout_token();
      this.up.clearStorage(true, false);

    } catch (error) {
			console.log(error)
    };
  }

  async confirmLogout() {
		let alert = await this.alertCtrl.create({
			header: this.languageService.logoutTranslate(this.language).title,
			message: this.languageService.logoutTranslate(this.language).msg,
			mode: "ios",
			buttons: [
				{
					text: this.languageService.cancelTranslate(this.language),
					role: "cancel",
				},
				{
					text: this.language === 'es' ? "Seguro" : "Log out",
					handler: () => {
						this.logout();
					},
				},
			],
		});
		await alert.present();
	}

	async logout() {
    try {
      await this.api.logout_token();
      this.up.clearStorage();
			this.router.navigate(["login"]);
    } catch (e) {
      console.log(e);
    };
	}

  // multimarca
  saveMultibrandApiUrls(resMaster) {
    const newApiUrl = {
      serviceBase: resMaster.url_workspace.api_url,
      serviceBaseGestor: resMaster.url_workspace.gestor_url,
      serviceBaseRest: resMaster.url_workspace.api_url + "rest/",
      serviceBaseAuth: resMaster.url_workspace.api_url + "auth/",
      urlBase: resMaster.url_workspace.app_url,
      urlReporte: resMaster.url_workspace.reportes_url,
      urlAndroid: resMaster.url_workspace.url_android,
      urliOS: resMaster.url_workspace.url_ios,
    };
    localStorage.setItem("api_urls", JSON.stringify(newApiUrl));
    this.api.setApiUrls = newApiUrl;
    // Ya autenticados, `api_urls` identifica la marca: la copia persistida de
    // `preLoginBrand` deja de ser necesaria y no debe quedar de vestigio.
    this.clearPersistedPreLoginBrand();
  }
  /*
  upgradeMultibrandApp(resMaster) {
    if (this.appPlatformService.isAppPlatform()) {
      if (this.platform.is("android"))
        this.multibrandAppVersionControl(
          environment.APP_VERSION_ANDROID,
          resMaster.url_workspace.android_version,
          resMaster.url_workspace.update_modal,
          resMaster.url_workspace.force_update
        );
      if (this.platform.is("ios"))
        this.multibrandAppVersionControl(
          environment.APP_VERSION_IOS,
          resMaster.url_workspace.ios_version,
          resMaster.url_workspace.update_modal,
          resMaster.url_workspace.force_update
        );
    }
  }

  multibrandAppVersionControl(userAppVersion, dataBaseVersion, showUpdateModal, forceAppUpdate) {
		//solo android, app iOS por publicar
		if (this.platform.is("ios")) return;

		const version_store = dataBaseVersion;
		const version_usu = userAppVersion;
		let ver_number_base_datos = parseInt(version_store.toString().split(".").join(""));
		let ver_number_usu = parseInt(version_usu.toString().split(".").join(""));
		console.log("v_BD", ver_number_base_datos);
		console.log("v_usu", ver_number_usu);
		if (ver_number_base_datos > ver_number_usu && showUpdateModal) {
			this.modalPlatformsUpgradeAvailable(forceAppUpdate, ver_number_base_datos);
		}
	}
    */

  async modalPlatformsUpgradeAvailable(isForcedToUpdate: boolean, dbVersion) {
		const modalUpgradeAvailable = await this.modalController.create({
			component: PlatformUpgradeAvailableComponent,
			backdropDismiss: false,
			cssClass: "platform-modal",
			componentProps: {
         isForcedToUpdate,
         dbVersion,
      },
		});
		await modalUpgradeAvailable.present();
		const { data } = await modalUpgradeAvailable.onWillDismiss();
		if (data?.confirm) {
      console.log("onWillDismiss", data)
      let appId = environment.APP_ID_IOS;
			try {
				if (this.platform.is("android")) {
          appId = environment.APP_ID_PLAYSTORE;
        };
        await NativeMarket.openStoreListing({
          appId: appId,
        });
        if(isForcedToUpdate) {
          setTimeout(() => {
            this.listenToAppStateChanges(dbVersion);
          }, 1000);
        }
			} catch (error) {
				console.log("error al abrir tienda::", error);
			};
		};
	}

  listenToAppStateChanges(dbVersion) {
    App.addListener('appStateChange', (state) => {
      console.log("*** appStateChange::", state)
      let userVersion = environment.APP_VERSION_ANDROID;
      if (this.platform.is("ios")){
        userVersion = environment.APP_VERSION_IOS;
      }
      let numberDbVersion = parseInt(dbVersion.toString().split(".").join(""));
			let numberUserVersion = parseInt(userVersion.toString().split(".").join(""));
      console.log("*** appStateChange number ver::", numberDbVersion, numberUserVersion )

      if (state.isActive ) {
        if(numberDbVersion > numberUserVersion) {
          this.modalPlatformsUpgradeAvailable(true, dbVersion);
          App.removeAllListeners();
        } else {
          App.removeAllListeners();
        }
      }
    });
  }

  getFcmToken() {
    if (this.appPlatformService.isAppPlatform()) {
      this.pushNotificationsService.requestPermissions();
    };
  }

  promptEnableNotificationsIfDenied() {
    this.pushNotificationsService.promptEnableNotificationsIfDenied();
  }

  redirectInductionPlatform() {
    const userData = JSON.parse(localStorage.getItem("user_data"));
    // if(this.up.getPlatformType === 'induction') {
      if(userData?.supervisor_induccion) {
        this.mp.setSelectedPageRoute("procesos");
				this.router.navigate(["procesos"]);
      }
      else {
        this.mp.setSelectedPageRoute("home");
        this.router.navigate(["home"]);
      }
    // };

  }

  /**
   * Guarda el tema de la marca recién logueada en `savedThemes`.
   *
   * La key es por **workspace** (`<id>-<slug>`) pero la lista de espacios es por
   * **marca**: la tarjeta muestra `titulo_login_app` + `extractSubdomain(serviceBase)`
   * y la credencial biométrica se aísla por `parseBaseUrl(serviceBase)`. Sin la
   * limpieza de abajo, entrar con usuarios de distintos workspaces de la misma
   * plataforma dejaba una tarjeta idéntica por workspace (y quitar una borraba la
   * biometría que compartían las gemelas, que seguían listadas).
   *
   * Invariante que garantiza este método: **una entrada por marca**. Su otra
   * mitad —los duplicados que ya están en dispositivos de producción— la limpia
   * `refreshAllThemes()` al leer.
   */
  public async getBaseThemeConfig(user) {
    const brandConfig = await this.api.getConfig();

    if(user?.workspace?.id) {
      try {
        const apiConnectors = this.api.getApiUrls;
        const savedThemes = JSON.parse(localStorage.getItem("savedThemes")) || {};
        const themeKey = `${user?.workspace?.id}-${user?.workspace?.slug}`;
        const baseTheme = {
          ...brandConfig,
           serviceBase: apiConnectors.serviceBase,  // ejemplo serviceBase: "https://gestiona.dev.cursalab.io/api/"
           workspaceId: user?.workspace?.id
        };

        // Cualquier entrada previa de ESTA marca (otro workspace de la misma
        // plataforma) se descarta: la nueva la reemplaza. `lastThemeUsed` se
        // reapunta unas líneas más abajo, así que no queda huérfano aunque
        // estuviera apuntando a la que se borra.
        //
        // La propia key también se borra antes de reasignarla: en JS reasignar
        // una key existente NO la mueve al final del objeto, y el orden de
        // inserción es lo que usa `dedupeSavedThemesByBrand` para saber cuál es
        // la más reciente.
        const brandKey = this.parseBaseUrl(apiConnectors.serviceBase);
        Object.keys(savedThemes).forEach(key => {
          if (this.parseBaseUrl(savedThemes[key]?.serviceBase) === brandKey) {
            delete savedThemes[key];
          }
        });
        delete savedThemes[themeKey];

        savedThemes[themeKey] = baseTheme;

        localStorage.setItem("savedThemes", JSON.stringify(savedThemes));
        localStorage.setItem("lastThemeUsed", themeKey);
      } catch (error) {
        console.log(error);
      };
    }

    return brandConfig;
  }

  /**
   * Deja una sola entrada por marca (`parseBaseUrl(serviceBase)`), conservando el
   * formato de key por workspace. Limpia los duplicados heredados: los que se
   * acumularon antes de que `getBaseThemeConfig` empezara a pisar la entrada
   * previa de la misma marca.
   *
   * Cuál sobrevive:
   *  1. la que apunta `lastThemeUsed`, si está en el grupo;
   *  2. si no, la última en orden de inserción (la más reciente).
   *
   * La regla 1 no es cosmética: es lo que garantiza que `lastThemeUsed` nunca
   * quede apuntando a una entrada descartada. Si eso pasara, `getThemesSavedBundle`
   * devolvería `null` y el usuario vería el selector vacío y el tema genérico de
   * Cursalab.
   *
   * NO borra credenciales biométricas (a diferencia de `removeSavedTheme`): las
   * gemelas comparten `serviceBase`, o sea la misma credencial, y la sobreviviente
   * la sigue necesitando.
   *
   * Da igual qué entrada gane en cuanto a contenido: todas se refrescan contra el
   * mismo `config_ambiente`. El único campo propio es `workspaceId`, que hoy no se
   * usa para nada funcional.
   */
  private dedupeSavedThemesByBrand(themes: Record<string, SavedTheme>): Record<string, SavedTheme> {
    const lastBrand = localStorage.getItem("lastThemeUsed");
    // brandKey -> key ganadora. Se recorre en orden de inserción, así que sin
    // `lastThemeUsed` de por medio gana la última vista.
    const winnerByBrand = new Map<string, string>();

    Object.keys(themes).forEach(key => {
      // Sin `serviceBase` no hay marca con la cual agrupar: se conserva tal cual
      // (grupo propio) para no perderla ni fusionarla con otra por error.
      const brandKey = themes[key]?.serviceBase
        ? this.parseBaseUrl(themes[key].serviceBase)
        : `__no-brand__${key}`;

      const currentWinner = winnerByBrand.get(brandKey);
      if (currentWinner === lastBrand) return; // la marca activa manda: no se pisa

      winnerByBrand.set(brandKey, key);
    });

    const winners = new Set(winnerByBrand.values());
    const deduped: Record<string, SavedTheme> = {};
    // Se reconstruye recorriendo `themes` (no el Set) para conservar el orden de
    // inserción original: `getThemesSavedBundle` pinta la lista en ese orden.
    Object.keys(themes).forEach(key => {
      if (winners.has(key)) deduped[key] = themes[key];
    });

    return deduped;
  }

  hasAtLeastOneSavedTheme(): boolean {
    const savedThemes = JSON.parse(localStorage.getItem("savedThemes") || "{}");
    return Object.keys(savedThemes).length > 0;
  }

  /**
   * Aplica el tema guardado de una marca (path C: selección desde el menú de
   * espacios de trabajo) y la deja como última marca usada.
   *
   * Limpia `preLoginBrand` para que la marca a autenticar salga de
   * `data_empresa.serviceBase` y no de un flujo previo (pre-login/subdominio).
   * Quien navegue después a otra vista puede volver a fijarlo si lo necesita.
   *
   * @returns `false` si esa marca no está guardada (no se aplicó nada).
   */
  applySavedThemeByKey(brandId: string): boolean {
    const savedThemes = JSON.parse(localStorage.getItem("savedThemes")) || {};

    if (!savedThemes[brandId]) return false;

    localStorage.setItem("lastThemeUsed", brandId);
    this.preLoginBrand = null;
    this.handleThemeMergeAndApplyFinalTheme(null, savedThemes[brandId], false);
    return true;
  }

  /**
   * Quita un espacio de trabajo de la lista guardada en ESTE dispositivo.
   *
   * `savedThemes` era solo aditivo (lo escribe `getBaseThemeConfig` tras cada
   * login exitoso): una marca visitada una vez quedaba en el selector para
   * siempre. Esto es la contraparte, disparada desde el menú de espacios.
   *
   * Es reversible —volver a agregar el espacio desde el pre-login lo reinserta—
   * pero arrastra tres efectos que hay que hacer juntos o el estado queda
   * inconsistente:
   *  - la credencial biométrica de esa marca (si no, quedaría una contraseña
   *    guardada de un espacio que el usuario acaba de quitar de su lista);
   *  - `lastThemeUsed`, que puede estar apuntando justo a la marca borrada;
   *  - `preLoginBrand`, que si es esa marca seguiría alimentando el `company`
   *    de `getActiveBrandCompany()`.
   *
   * NO repinta el tema: eso lo decide la página (ver `applyGenericTheme`).
   *
   * @returns `nextThemeKey` = nuevo `lastThemeUsed` (`null` si no queda ninguna).
   */
  async removeSavedTheme(brandId: string): Promise<{
    removed: boolean;
    nextThemeKey: string | null;
    remaining: number;
  }> {
    const savedThemes: Record<string, SavedTheme> = this.readStoredJson("savedThemes") || {};

    if (!savedThemes[brandId]) {
      return {
        removed: false,
        nextThemeKey: localStorage.getItem("lastThemeUsed"),
        remaining: Object.keys(savedThemes).length
      };
    }

    // El brandKey de la credencial se deriva del serviceBase, así que hay que
    // leerlo ANTES de borrar la entrada. Misma normalización que usó el
    // enrolamiento (`getActiveBrandKey` / `getCurrentBrandKey`).
    const removedServiceBase = savedThemes[brandId].serviceBase;
    const removedBrandKey = removedServiceBase ? this.parseBaseUrl(removedServiceBase) : null;

    delete savedThemes[brandId];
    const remainingKeys = Object.keys(savedThemes);
    localStorage.setItem("savedThemes", JSON.stringify(savedThemes));

    // `clear()` es no-op fuera de app nativa, así que no hace falta guarda extra.
    if (removedBrandKey) {
      try {
        await this.biometric.clear(removedBrandKey);
      } catch (error) {
        // Perder la credencial no debe abortar el borrado del espacio.
        console.log("removeSavedTheme biometric clear failed::", error);
      }
    }

    const nextThemeKey = remainingKeys[0] || null;
    if (localStorage.getItem("lastThemeUsed") === brandId) {
      nextThemeKey
        ? localStorage.setItem("lastThemeUsed", nextThemeKey)
        : localStorage.removeItem("lastThemeUsed");
    }

    // La marca borrada era la que el pre-login/subdominio dejó resuelta: sin
    // esto el login seguiría autenticando contra ella.
    const activeBaseUrl = this.preLoginBrand?.baseUrl;
    if (activeBaseUrl && removedBrandKey && this.parseBaseUrl(activeBaseUrl) === removedBrandKey) {
      this.preLoginBrand = null;
    }

    return {
      removed: true,
      nextThemeKey: localStorage.getItem("lastThemeUsed"),
      remaining: remainingKeys.length
    };
  }

  /**
   * Vuelve al tema genérico de Cursalab. Se usa cuando el usuario quitó la
   * última marca guardada: ya no hay ninguna que pintar.
   *
   * Borra `config_ambiente` antes de aplicar para no dejar el tema de la marca
   * recién quitada como respaldo de un arranque en frío; `buildFinalTheme` lo
   * vuelve a persistir, ahora con el genérico.
   */
  applyGenericTheme() {
    localStorage.removeItem("config_ambiente");
    clearThemeSnapshot();
    this.handleThemeMergeAndApplyFinalTheme(null, this.themeColorsService.defaultConfigData, false);
  }

  getThemesSavedBundle(themes: Record<string, SavedTheme>) {
    // const themes:object = JSON.parse(localStorage.getItem("savedThemes")) || {};
    const lastBrand = localStorage.getItem("lastThemeUsed");

    const savedThemes = Object.entries(themes).map(([key, value]) => ({
      key,
      ...value
    }));

    if (lastBrand) {
      savedThemes.sort((a, b) => {
        if (a.key === lastBrand) return -1;
        if (b.key === lastBrand) return 1;
        return 0;
      });
    }

    if (lastBrand && themes[lastBrand]) {
      return {
        lastThemeObj : {key: lastBrand, ...themes[lastBrand] },
        savedThemes : savedThemes
      };
    };

    return null; // Mostrar login genérico
  }

  // async loadInitialTheme() {
  //   const themes = JSON.parse(localStorage.getItem("savedThemes") || "{}");
  //   const lastBrand = localStorage.getItem("lastThemeUsed");

  //   if (!lastBrand || !themes[lastBrand]) {
  //     return null; // Sin tema → mostrar login genérico
  //   }

  //   const serviceBase = themes[lastBrand].serviceBase;
  //   const workspaceId = themes[lastBrand].workspaceId;

  //   const freshData = await this.api.getBaseConfig(workspaceId, serviceBase);

  //    themes[lastBrand] = { ...themes[lastBrand], ...freshData };
  //    console.log("fresh themes::", themes);
  //    localStorage.setItem("savedThemes", JSON.stringify(themes));

  //   return freshData;
  // }



  async getLastThemeUsed() {
    console.log("get Last Theme Used called!");
    const themes =  JSON.parse(localStorage.getItem("savedThemes")) || {};
    const lastBrand = localStorage.getItem("lastThemeUsed");

    if (lastBrand && themes[lastBrand]) {
      const serviceBase = themes[lastBrand].serviceBase;
      const workspaceId = themes[lastBrand].workspaceId;

      // this.api.getEnviromentConfig(serviceBase, null, workspaceId).subscribe(
      //   {
      //     next: async (configByWorkspaceId) => {
      //       console.log("login getEnviromentConfig::",configByWorkspaceId)
      //     },
      //     error: (e) => {
      //       console.log(e);
      //     }
      //   }
      // );


      const freshData = await this.api.getBaseConfig(serviceBase);
      //! todo: limpiar workspace_name, fondo_app
      themes[lastBrand] = {
         ...themes[lastBrand],
         ...freshData
      };
      // console.log("fresh last theme used::", themes);
      // localStorage.setItem("savedThemes", JSON.stringify(themes));

      return {
        key: lastBrand,
        ...themes[lastBrand]
      };
    };

    return null;
  }

  refreshAllThemes() {
    const stored: Record<string, SavedTheme> = JSON.parse(localStorage.getItem("savedThemes") || "{}");

    // Punto único por el que pasan login y pre-login antes de pintar la lista, así
    // que es donde se limpian los duplicados heredados (una entrada por workspace
    // de la misma marca). Se persiste ya —sin esperar al forkJoin— para que la
    // limpieza no dependa de que la red responda; de paso el forkJoin queda con un
    // GET por marca y no uno por entrada.
    const themes = this.dedupeSavedThemesByBrand(stored);
    if (Object.keys(themes).length !== Object.keys(stored).length) {
      localStorage.setItem("savedThemes", JSON.stringify(themes));
    }

    if (Object.keys(themes).length === 0) return of({});
    try {
      const requests = Object.entries(themes).map(([brand, config]) =>
        this.http.get(`${config.serviceBase}config_ambiente`).pipe(
          map(fresh => ({ brand, fresh })),
          // Blindaje 2: si una marca falla (serviceBase caído/inválido), no
          // tumbamos el forkJoin entero. Devolvemos su tema cacheado sin refrescar.
          catchError(error => {
            console.log("refreshAllThemes brand refresh failed::", brand, error);
            return of({ brand, fresh: null });
          })
        )
      );

      return forkJoin(requests).pipe(
        map(results => {
          results.forEach(({ brand, fresh }) => {
            if (!fresh) return; // el refresco de esta marca falló: conservamos su cache
            // Blindaje 1: serviceBase/workspaceId se inyectan a mano y NO vienen
            // de config_ambiente. Se reinyectan tras el merge para que una futura
            // respuesta con serviceBase (o null) nunca los sobrescriba/persista.
            themes[brand] = {
              ...themes[brand],
              ...fresh,
              serviceBase: themes[brand].serviceBase,
              workspaceId: themes[brand].workspaceId
            };
          });

          localStorage.setItem("savedThemes", JSON.stringify(themes));
          // console.log("servicio Refreshed themes:", themes);
          return themes;
        })
      );

    } catch (error) {
      console.log("error::",error)
      return of({});
    };
  }


  // getLastThemeUsed() {
  //   const themes =  JSON.parse(localStorage.getItem("savedThemes")) || {};
  //   const lastBrand = localStorage.getItem("lastThemeUsed");

  //   if (lastBrand && themes[lastBrand]) {
  //     return {
  //       key: lastBrand,
  //       ...themes[lastBrand]
  //     };
  //   };

  //   return null;
  // }

  /** Host desde el que se sirve la app genérica (sin marca fijada por la URL). */
  private static readonly DEFAULT_BRAND_HOST = "app.cursalab.io";

  /**
   * El login se está sirviendo desde el dominio propio de una marca
   * (`producto.cursalab.io`). Solo puede ser true en web: en Capacitor
   * `window.location` es `capacitor://localhost` y no hay subdominio custom.
   */
  isCustomBrandDomain(hostname: string = window.location.hostname): boolean {
    if (this.appPlatformService.isAppPlatform()) return false;

    return hostname !== LoginMultiModuloService.DEFAULT_BRAND_HOST
      && !!this.extractSubdomainOnly(hostname);
  }

  /**
   * El camino B (resolver la marca por subdominio) va a correr y no hay tema de
   * marca cacheado que pintar mientras tanto: lo único que se podría mostrar
   * ahora es el genérico de Cursalab, que después se repintaría en vivo.
   *
   * Las tres guardas replican la condición de camino B en `login.page.ts`
   * (`ionViewWillEnter`): si una cambia, esta también.
   */
  isBrandThemePendingForCustomDomain(hostname: string = window.location.hostname): boolean {
    if (!environment.APP_MULTIMARCA) return false;
    if (!this.isCustomBrandDomain(hostname)) return false;
    // Marca ya resuelta (venimos del pre-login): camino B no vuelve a consultar.
    if (this.preLoginBrand?.baseUrl) return false;
    // Visita repetida: el tema de la marca se pinta desde cache, sin red.
    if (localStorage.getItem("config_ambiente")) return false;

    return true;
  }

  extractSubdomainOnly(hostname: string): string {
    if (!hostname) return '';

    // Divide el hostname por puntos
    const parts = hostname.split('.');

    // Si tiene al menos 3 partes (ej: abc.cursalab.io), devuelve la primera
    if (parts.length >= 3) {
      return parts[0];
    }

    return '';
  }

  parseBaseUrl(url: string): string {
    if (!url || typeof url !== 'string') {
      //console.warn('parseBaseUrl: URL inválida', url);
      return '';
    }

    try {
     // console.log('parseBaseUrl: parseando URL', new URL(url).origin + '/');
      return new URL(url).origin + '/';
    } catch {
      console.warn('parseBaseUrl: no se pudo parsear la URL', url);
      return url; // fallback: devolvemos el string original tal cual
    }
  }

  /**
   * Clave de marca (brandKey) de la sesión activa, para asociar la credencial
   * biométrica a la marca correcta desde pantallas EN SESIÓN (perfil, cambio de
   * contraseña). Se deriva del `serviceBase` de la marca logueada (guardado en
   * `api_urls` por `saveMultibrandApiUrls`) y se normaliza igual que el `company`
   * que envía el login, de modo que coincide con el brandKey usado al enrolar.
   *
   * En apps de marca única devuelve `null` => el servicio biométrico usa su
   * credencial única (comportamiento histórico).
   */
  getCurrentBrandKey(): string | null {
    if (!environment.APP_MULTIMARCA) return null;
    try {
      const apiUrls = JSON.parse(localStorage.getItem("api_urls") || "{}");
      const serviceBase = apiUrls?.serviceBase;
      return serviceBase ? (this.parseBaseUrl(serviceBase) || null) : null;
    } catch {
      return null;
    }
  }

  private readStoredJson(key: string): any {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  /**
   * Valor que se envía como `company` a los endpoints del master (URL_MASTER),
   * que por sí solos no saben a qué marca pertenece el usuario: login,
   * recuperar contraseña, ayuda al coordinador, SSO, validación del token del
   * enlace de correo y notificaciones pre-login.
   *
   * Fuente única de verdad: los pasos 1-2 son los que ya usaba el login, y los
   * 3-4 son los respaldos persistidos que hacen que el valor sobreviva a una
   * recarga o a un deep link.
   *
   * En apps de marca única devuelve `null` => los llamadores no envían `company`
   * y el payload queda idéntico al histórico.
   */
  /**
   * ¿El tema pintado ahora pertenece a una marca distinta de `lastThemeUsed`?
   *
   * `clearStorage()` emite "usuario no logueado" no solo en el logout: también
   * al *iniciar* un login (`login.page.ts` limpia el storage antes de
   * autenticar). Ese evento hace que `app.component` repinte el tema de la
   * última marca logueada, lo que en el path pre-login/subdominio (marca aún no
   * guardada en `savedThemes`) devolvía el login a la marca anterior mientras el
   * usuario seguía en la pantalla de la marca nueva.
   *
   * Devuelve `true` cuando hay una marca activa (`preLoginBrand`) que no
   * corresponde a `lastThemeUsed` y ya hay un tema aplicado: en ese caso el
   * repintado "no logueado" debe respetar lo que está en pantalla.
   */
  appliedThemeBelongsToOtherBrand(): boolean {
    if (!environment.APP_MULTIMARCA) return false;

    // Arranque en frío: no hay nada pintado que conservar.
    if (!this.up.configBrand?.value) return false;

    // Path "marca guardada" (C): la marca activa ES `lastThemeUsed`.
    const activeBaseUrl = this.preLoginBrand?.baseUrl;
    if (!activeBaseUrl) return false;

    const savedThemes = this.readStoredJson("savedThemes") || {};
    const lastBrand = localStorage.getItem("lastThemeUsed");
    const lastServiceBase = lastBrand ? savedThemes[lastBrand]?.serviceBase : null;

    // Marca activa sin tema guardado que la respalde (primer login en ella).
    if (!lastServiceBase) return true;

    return this.parseBaseUrl(lastServiceBase) !== this.parseBaseUrl(activeBaseUrl);
  }

  getActiveBrandCompany(): string | null {
    if (!environment.APP_MULTIMARCA) return null;

    // 1. Marca escrita en pre-login o resuelta por subdominio (ya rehidratada).
    //    Se devuelve tal cual, sin normalizar, para ser idéntica al `company`
    //    que el login viene enviando.
    const preLoginBaseUrl = this.preLoginBrand?.baseUrl;
    if (preLoginBaseUrl) return preLoginBaseUrl;

    // 2. Marca cuyo tema está aplicado ahora (path "marca guardada"), en memoria
    //    o desde `config_ambiente` si venimos de una recarga.
    const appliedServiceBase = this.up.configBrand?.value?.serviceBase
      || this.readStoredJson("config_ambiente")?.serviceBase;
    if (appliedServiceBase) return this.parseBaseUrl(appliedServiceBase) || null;

    // 3. Última marca logueada.
    const savedThemes = this.readStoredJson("savedThemes") || {};
    const lastBrand = localStorage.getItem("lastThemeUsed");
    const lastServiceBase = lastBrand ? savedThemes[lastBrand]?.serviceBase : null;
    if (lastServiceBase) return this.parseBaseUrl(lastServiceBase) || null;

    // 4. En sesión, desde `api_urls`.
    return this.getCurrentBrandKey();
  }


}
